import { setDataAttribute, setValue as controlsSetValue, enable as controlsEnable, disable as controlsDisable } from "./skyward.ui.controls";
import { registerEvents } from "./skyward.ui.events";

export type RadioButtonGroupObject =
{
    groupElement: JQuery<HTMLElement>,
    valueElement: JQuery<HTMLElement>,
    radioInputs: JQuery<HTMLElement>,
    radioButtons: JQuery<HTMLElement>,
    originalValue: string,
    getCheckedRadioButton: () => JQuery<HTMLElement>,
    getValue: () => string,
};

const skyward = window.skyward;
skyward.radioButtonGroup = skyward.radioButtonGroup || {};

const classNames =
{
    radioButtonGroup: "radioButtonGroup",
    radioButtonGroupValue: "radioButtonGroupValue",
    radioButton: "radioButton",
};

const events =
{
    init
};

registerEvents("radioButtonGroup", events);

skyward.radioButtonGroup.getRadioButtonGroup = getRadioButtonGroup;
export function getRadioButtonGroup(groupElement: JQuery<HTMLElement>)
{
    const radioButtonGroupValue = groupElement.find(`.${classNames.radioButtonGroupValue}`);
    const name = groupElement.data().name;
    const radioButtonSelector = 'input:radio[name="' + name + 'child"]';
    const radioButtons = $(radioButtonSelector) as JQuery<HTMLInputElement>;

    return getRadioButtonObject(groupElement, radioButtonGroupValue, radioButtons);
}

skyward.radioButtonGroup.enable = enable;
export function enable(control: JQuery<HTMLElement>)
{
    const groupElement = getRadioButtonGroupElement(control);
    const group = getRadioButtonGroup(groupElement);
    if (group)
    {
        controlsEnable(group.radioInputs);
    }
}

skyward.radioButtonGroup.disable = disable;
export function disable(control: JQuery<HTMLElement>)
{
    const groupElement = getRadioButtonGroupElement(control);
    const group = getRadioButtonGroup(groupElement);
    if (group)
    {
        controlsDisable(group.radioInputs);
    }
}

skyward.radioButtonGroup.hide = hide;
export function hide(control: JQuery<HTMLElement>)
{
    const groupElement = getRadioButtonGroupElement(control);
    const group = getRadioButtonGroup(groupElement);
    if (group)
    {
        group.radioButtons.hide();
    }
}

skyward.radioButtonGroup.show = show;
export function show(control: JQuery<HTMLElement>)
{
    const groupElement = getRadioButtonGroupElement(control);
    const group = getRadioButtonGroup(groupElement);
    if (group)
    {
        group.radioButtons.show();
        return group.radioButtons;
    }
    return $();
}

skyward.radioButtonGroup.getValue = getValue;
export function getValue(control: JQuery<HTMLElement>)
{
    const groupElement = getRadioButtonGroupElement(control);
    const group = getRadioButtonGroup(groupElement);
    if (group)
    {
        return group.getValue();
    }

    return undefined;
}

skyward.radioButtonGroup.getOriginalValue = getOriginalValue;
export function getOriginalValue(control: JQuery<HTMLElement>)
{
    const groupElement = getRadioButtonGroupElement(control);
    const group = getRadioButtonGroup(groupElement);
    if (group)
    {
        return group.originalValue;
    }

    return undefined;
}

skyward.radioButtonGroup.setValue = setValue;
export function setValue(control: JQuery<HTMLElement>, value: string, supressChangeEvent?: boolean)
{
    const groupElement = getRadioButtonGroupElement(control);
    const group = getRadioButtonGroup(groupElement);
    if (group)
    {
        const checkedButtonValue = group.getCheckedRadioButton().val();
        if (checkedButtonValue != value)
        {
            controlsSetValue(group.radioInputs, value, supressChangeEvent);
        }

        group.valueElement.val(value);
        if (!supressChangeEvent)
        {
            group.valueElement.trigger("change");
        }
    }
}

function init(selector: HTMLElement | JQuery<HTMLElement>)
{
    let radioButtonGroup = $(selector);
    let radioButtonGroupValue = radioButtonGroup.find(`.${classNames.radioButtonGroupValue}`);
    const name = radioButtonGroup.data().name;
    const radioButtonSelector = 'input:radio[name="' + name + 'child"]';
    const radioButtons = $(radioButtonSelector);

    setDependencyAttributes();

    radioButtons.on("change", function ()
    {
        const button = $(this);
        radioButtonGroup = button.parents(`.${classNames.radioButtonGroup}`);
        radioButtonGroupValue = radioButtonGroup.find(`.${classNames.radioButtonGroupValue}`);
        controlsSetValue(radioButtonGroupValue, button.val());

        setDependencyAttributes();
    });

    if (!radioButtonGroup.attr("aria-label") && !radioButtonGroup.attr("aria-labelledby"))
    {
        let label = $.findLabel(name);
        if (label.length == 0)
        {
            label = radioButtonGroupValue.findLabel();
        }

        if (label.length > 0)
        {
            const id = label.addIds().attr("id")!;
            radioButtonGroup.attr("aria-labelledby", id);
        }
    }

    function setDependencyAttributes()
    {
        const radioData = $(radioButtonSelector + ':checked').data();
        for (const key in radioData)
        {
            if (key.lastIndexOf('dependencyattribute', 0) === 0)
            {
                setDataAttribute(radioButtonGroupValue, key, radioData[key]);
            }
        }
    }
}

function getRadioButtonObject(radioButtonGroup: JQuery<HTMLElement>, radioButtonGroupValue: JQuery<HTMLElement>, radioInputs: JQuery<HTMLInputElement>)
{
    const obj: RadioButtonGroupObject =
    {
        groupElement: radioButtonGroup,
        valueElement: radioButtonGroupValue,
        radioInputs: radioInputs,
        radioButtons: radioInputs.closest(`.${classNames.radioButton}`),
        originalValue: radioButtonGroupValue.data().originalValue,
        getCheckedRadioButton: function ()
        {
            return obj.radioInputs.filter(':checked');
        },
        getValue: function ()
        {
            return obj.valueElement.val() as string;
        }
    };

    return obj;
}

function getRadioButtonGroupElement(element: JQuery<HTMLElement>)
{
    if (element.hasClass(classNames.radioButtonGroup))
    {
        return element;
    }

    return element.closest(`.${classNames.radioButtonGroup}`);
}